import { FC, useContext, useState } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { Box, Stack } from "@mui/material";
import { RecaptchaGuardMessage, RouteBuilders } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { serializeError } from "serialize-error";

import { AmplitudeEvents } from "../../../../../../../common/constants/amplitude";
import { RecaptchaAction } from "../../../../../../../common/constants/recaptcha";
import { useAmplitude } from "../../../../../../../common/hooks/amplitude/useAmplitude";
import { useRecaptchaRetry } from "../../../../../../../common/hooks/useRecaptchaRetry";
import { useTranslate } from "../../../../../../../common/hooks/useTranslate";
import { useRegisterNavigator } from "../../../hooks/useRegisterNavigator";
import { SignUpSessionStorage } from "../../../store/SignUpSessionStorage";

import { trpc } from "@/common/helpers/trpc";
import { isRequired } from "@/common/helpers/validators";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardEmailTextField } from "@/dashboard-design-system/EmailTextField";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { RegisterSubmissionContext } from "@/features/Auth/Register/Context/RegisterStepContext";

interface EmailFormData {
  email: string;
}

export const EmailSignUpForm: FC = () => {
  const { t } = useTranslate();
  const router = useRouter();
  const buildUrl = useUrlBuilder();
  const { logAmplitudeEvent } = useAmplitude();
  const { executeTrpc } = useRecaptchaRetry();
  const isUserEmailRegistered = trpc.isUserEmailRegistered.useMutation();
  const { goToUserInfo } = useRegisterNavigator();
  const submitOnce = useContext(RegisterSubmissionContext);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const defaultEmail = useSearchParams().get("email") || "";

  const form = useForm<EmailFormData>({
    defaultValues: {
      email: defaultEmail,
    },
    mode: "onBlur",
  });

  const { errors } = form.formState;
  const email = form.watch("email");

  const handleSubmit = form.handleSubmit((data: EmailFormData) =>
    submitOnce(async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        // Check if the email is already registered
        const action = RecaptchaAction.IsEmailRegistered;
        const { data: result, error } = await executeTrpc(action, data.email, isUserEmailRegistered.mutateAsync, {
          email: data.email,
        });

        if (error) {
          const isRecaptchaError = error.code === RecaptchaGuardMessage;
          datadogLogs.logger.error("[SignUp] isUserEmailRegistered rejected", {
            browserErrorEmail: data.email,
            isRecaptchaError,
            error,
          });
          setErrorMessage(
            t("register", isRecaptchaError ? "signin.recaptchaErrorOnIsEmailRegistered" : "common.gqlError")
          );
          setIsLoading(false);
          return;
        }

        if (result?.isExistingUser) {
          // If the user is already registered, redirect to sign in page
          logAmplitudeEvent(AmplitudeEvents.UserSignUpRedirectedToSignIn, { email: data.email });
          await router.push(buildUrl({ route: RouteBuilders.buildSignInLink({ email: data.email }) }));
          return;
        }

        // User is not registered - continue with signup
        logAmplitudeEvent(AmplitudeEvents.UserSignUpInputChanged, {
          input_name: "user_email",
          field_empty: !data.email,
        });

        // Save email to session storage with verification cache
        SignUpSessionStorage.update({
          email: data.email,
          emailVerifiedAt: Date.now(),
          isEmailVerified: true, // User doesn't exist, verified as new
        });

        // Navigate to user info page
        await goToUserInfo();
      } catch (error) {
        datadogLogs.logger.error("[SignUp] email step failed", {
          browserErrorEmail: data.email,
          error: serializeError(error),
        });
        setErrorMessage(t("register", "common.gqlError"));
      } finally {
        setIsLoading(false);
      }
    })
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <DashboardEmailTextField
          data-test="signup-email-field"
          id="email"
          label={t("register", "signup.emailAddress")}
          name="email"
          autoComplete="email"
          control={form.control}
          errorMessage={errors["email"]?.message}
          rules={[isRequired(t("register", "signin.emailIsRequired") as string)]}
          checkMisspelledEmail
          placeholder={t("register", "signup.emailPlaceholder")}
          onBlur={() => {
            logAmplitudeEvent(AmplitudeEvents.UserSignUpInputChanged, {
              input_name: "user_email",
              valid_field: !errors["email"],
              field_empty: !form.getValues("email"),
            });
          }}
        />

        {errorMessage && <DashboardInfobox vibe="danger">{errorMessage}</DashboardInfobox>}

        <DashboardButton
          type="submit"
          fullWidth
          variant="filled"
          size="large"
          vibe="brand"
          data-test="signup-email-continue-button"
          disabled={!email || isLoading}
          isLoading={isLoading}
        >
          {t("common", "Continue")}
        </DashboardButton>
      </Stack>
    </Box>
  );
};
